/**
 * Expandable Text Component
 *
 * Reusable component for text that can be expanded/collapsed with a "Show More" button
 * Follows SOLID principles:
 * - Single Responsibility: Only responsible for rendering expandable text
 * - Open/Closed: Open for extension via props, closed for modification
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  typographyColors,
  spacing,
  typography,
  fontPrimary,
} from '@/design-system/utils';
import { ShowMoreTextButton } from './show-more-text-button';
import { useTextOverflow } from '@/hooks/useTextOverflow';

export interface ExpandableTextProps {
  /**
   * The text content to display
   */
  text: string;
  /**
   * Maximum number of lines to show when collapsed
   * @default 2
   */
  maxLines?: number;
  /**
   * Font size override
   */
  fontSize?: string;
  /**
   * Font weight override
   */
  fontWeight?: number;
  /**
   * Text color override
   */
  color?: string;
  /**
   * Line height override
   */
  lineHeight?: string;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Additional inline styles
   */
  style?: React.CSSProperties;
}

/**
 * Expandable Text Component
 * Renders text that can be expanded/collapsed with ellipsis
 */
export const ExpandableText: React.FC<ExpandableTextProps> = ({
  text,
  maxLines = 2,
  fontSize = typography.size.sm,
  fontWeight = typography.weight.regular,
  color = typographyColors.text,
  lineHeight = typography.lineHeight.relaxed,
  className = '',
  style = {},
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { textRef, exceedsLines } = useTextOverflow<HTMLParagraphElement>(
    maxLines,
    text
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[1],
        flexGrow: 1,
      }}
      className={className}
    >
      <motion.p
        ref={textRef}
        animate={{
          WebkitLineClamp: isExpanded ? 'none' : maxLines,
        }}
        transition={{
          duration: 0.3,
          ease: [0.4, 0, 0.2, 1],
        }}
        style={{
          fontFamily: fontPrimary,
          fontSize,
          fontWeight,
          color,
          lineHeight,
          margin: 0,
          display: '-webkit-box',
          WebkitLineClamp: isExpanded ? 'none' : maxLines,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          ...style,
        }}
      >
        {text}
      </motion.p>
      {exceedsLines && (
        <ShowMoreTextButton
          isExpanded={isExpanded}
          onClick={() => setIsExpanded(!isExpanded)}
          style={isExpanded ? { marginTop: 'auto' } : {}}
        />
      )}
    </div>
  );
};
